import React, { useEffect, useRef } from "react";

interface FocusTrapProps {
  /**
   * If initially focused element is not focusable
   * (e.g. due to `tabindex="-1"`), ensure focus
   * is still trapped.
   */
  initialFocusElementRef?: React.RefObject<HTMLElement | null>;
  children: React.ReactNode;
}

function useFocusTrap(
  initiallyFocusedElement: HTMLElement | null = null,
): React.RefObject<HTMLDivElement | null> {
  const elRef = useRef<HTMLDivElement | null>(null);

  function handleFocus(e: KeyboardEvent): void {
    if (elRef.current) {
      const focusableEls = elRef.current.querySelectorAll<HTMLElement>(
          "a[href], button, textarea, input, select",
        ),
        firstFocusableEl = focusableEls[0],
        lastFocusableEl = focusableEls[focusableEls.length - 1];

      if (e.key !== "Tab") {
        return;
      }

      if (e.shiftKey && lastFocusableEl) {
        // shift + tab
        if (
          document.activeElement === firstFocusableEl ||
          document.activeElement === initiallyFocusedElement
        ) {
          lastFocusableEl.focus();
          e.preventDefault();
        }
      } else {
        // tab
        if (
          (document.activeElement === lastFocusableEl ||
            document.activeElement === initiallyFocusedElement) &&
          firstFocusableEl
        ) {
          firstFocusableEl.focus();
          e.preventDefault();
        }
      }
    }
  }

  useEffect(() => {
    elRef.current?.addEventListener("keydown", handleFocus);

    return () => {
      elRef.current?.removeEventListener("keydown", handleFocus);
    };
  }, [initiallyFocusedElement]);

  return elRef;
}

export function FocusTrap({
  initialFocusElementRef,
  children,
}: FocusTrapProps): React.JSX.Element {
  const elRef = useFocusTrap(initialFocusElementRef?.current);

  return <div ref={elRef}>{children}</div>;
}
